// src/App.jsx
import { useState, useEffect } from "react";
import ReceiveFlow from "./components/ReceiveFlow";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AdminPage from "./components/AdminPage";
import FloatingPets from "./components/FloatingPets";
import AboutPage from "./components/AboutPage";
import TermsPage from "./components/TermsPage";
import StickyAd from "./components/StickyAd";
import "./styles/global.css";

// BrowserRouter 방식 (History API 직접 사용 - AdSense/SEO 크롤 가능한 개별 URL 생성)
// 사용: navigate("/about") / 링크 <a href="/about" onClick={handleNavClick}>
export function navigate(path) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function handleNavClick(e) {
  // 내부 링크 가로채기 (새창/수정키 제외)
  if (e.defaultPrevented) return;
  if (e.button !== 0) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const anchor = e.currentTarget;
  const href = anchor.getAttribute("href");
  if (!href || !href.startsWith("/")) return;
  e.preventDefault();
  navigate(href);
}

function pathToPage(pathname) {
  if (pathname === "/admin") return "admin";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/about") return "about";
  if (pathname === "/terms") return "terms";
  return "home";
}

export default function App() {
  const [started, setStarted] = useState(false);
  const [page, setPage] = useState(() => pathToPage(window.location.pathname));

  // History API 기반 라우팅 (popstate + 초기 해시 마이그레이션)
  useEffect(() => {
    // 레거시 해시 URL 들어오면 경로로 리다이렉트 (#/about -> /about)
    const hash = window.location.hash;
    if (hash && hash.startsWith("#/")) {
      const newPath = hash.slice(1) || "/";
      window.history.replaceState({}, "", newPath);
      setPage(pathToPage(newPath));
    }
    const onPop = () => setPage(pathToPage(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // 홈으로 돌아올 때 기본 메타 복원 (서브페이지는 각 컴포넌트가 usePageMeta로 설정)
  useEffect(() => {
    if (page !== "home") return;
    document.title = "펫말 - 반려동물이 전하는 편지 🐾";
    const d = document.head.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", "말 못 하는 게 아니라, 말할 기회가 없었던 거예요. 반려동물과의 추억을 들려주시면, 그 친구의 마음을 편지로 전해드립니다.");
    const c = document.head.querySelector('link[rel="canonical"]');
    if (c) c.setAttribute("href", "https://www.petmal.kr/");
  }, [page]);

  const goHome = () => navigate("/");

  // FloatingPets(나비)와 StickyAd(상시 배너)는 페이지가 바뀌어도 항상 유지
  const renderPage = () => {
    if (page === "admin") return <AdminPage />;
    if (page === "about") return <AboutPage onBack={goHome} />;
    if (page === "terms") return <TermsPage onBack={goHome} />;
    if (page === "privacy") return <PrivacyPolicy onBack={goHome} />;
    return renderHome();
  };

  const renderHome = () => (
    <>
      {/* 헤더 */}
      <div style={{ textAlign: "center", padding: started ? "32px 24px 16px" : "56px 24px 16px", transition: "padding .4s ease" }}>
        <span style={{ fontSize: started ? 32 : 52, display: "block", marginBottom: 8, animation: "float 3s ease-in-out infinite", transition: "font-size .4s ease" }}>
          🐾
        </span>
        <div style={{
          fontFamily: "'Jua', sans-serif",
          fontSize: started ? 32 : 48,
          fontWeight: 700,
          color: "#5c3d2e",
          letterSpacing: -1,
          marginBottom: 6,
          transition: "font-size .4s ease",
        }}>
          펫말
        </div>
        {!started && (
          <div style={{ fontSize: 16, fontWeight: 400, color: "#b09070", lineHeight: 1.9, animation: "fadeUp .5s ease" }}>
            말 못 하는 게 아니라, 말할 기회가 없었던 거예요.<br />
            오늘 그 친구가 드디어 말을 해요.
          </div>
        )}
      </div>

      {!started ? (
        <Landing onStart={() => setStarted(true)} />
      ) : (
        <ReceiveFlow onBack={() => setStarted(false)} />
      )}
    </>
  );

  return (
    <>
      <FloatingPets />
      <StickyAd />
      <div style={{ minHeight: "100vh", background: "transparent", paddingBottom: 150, position: "relative", zIndex: 1 }}>
        {renderPage()}
      </div>
    </>
  );
}

function Landing({ onStart }) {
  return (
    <div style={{
      maxWidth: 400,
      margin: "0 auto",
      padding: "24px 24px 0",
      textAlign: "center",
      animation: "fadeUp .5s ease",
    }}>
      {/* 팻말 (나무 표지판) */}
      <div style={{ position: "relative", display: "inline-block", marginTop: 8 }}>

        {/* 나무 기둥 상단 */}
        <div style={{
          width: 16,
          height: 40,
          background: "linear-gradient(180deg, #b8956a, #a07848)",
          borderRadius: "4px 4px 0 0",
          margin: "0 auto",
          position: "relative",
          zIndex: 0,
          boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
        }} />

        {/* 표지판 판넬 */}
        <div style={{
          background: `
            linear-gradient(170deg, #c89050, #b87840),
            repeating-linear-gradient(
              90deg,
              transparent, transparent 40px,
              rgba(0,0,0,0.03) 40px, rgba(0,0,0,0.03) 41px
            )
          `,
          borderRadius: 14,
          padding: "40px 56px 36px",
          position: "relative",
          top: -4,
          zIndex: 1,
          boxShadow: `
            0 8px 32px rgba(92,61,46,0.25),
            inset 0 2px 0 rgba(255,255,255,0.15),
            inset 0 -2px 4px rgba(0,0,0,0.1)
          `,
          border: "3px solid #a06830",
          minWidth: 320,
        }}>
          {/* 나무결 질감 오버레이 */}
          <div style={{
            position: "absolute", inset: 0, borderRadius: 11,
            background: "repeating-linear-gradient(175deg, transparent, transparent 8px, rgba(255,255,255,0.04) 8px, rgba(255,255,255,0.04) 9px)",
            pointerEvents: "none",
          }} />

          {/* 나무 못 왼쪽 */}
          <div style={{
            position: "absolute", top: 14, left: 18,
            width: 10, height: 10, borderRadius: "50%",
            background: "radial-gradient(circle at 40% 40%, #a08050, #706030)",
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.4), 0 1px 1px rgba(255,255,255,0.1)",
          }} />
          {/* 나무 못 오른쪽 */}
          <div style={{
            position: "absolute", top: 14, right: 18,
            width: 10, height: 10, borderRadius: "50%",
            background: "radial-gradient(circle at 40% 40%, #a08050, #706030)",
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.4), 0 1px 1px rgba(255,255,255,0.1)",
          }} />

          <span style={{ fontSize: 48, display: "block", marginBottom: 14 }}>💌</span>
          <div style={{
            fontFamily: "'Jua', sans-serif",
            fontSize: 26,
            fontWeight: 700,
            color: "#fff",
            textShadow: "0 2px 4px rgba(60,30,10,0.5)",
            marginBottom: 8,
            position: "relative",
          }}>
            편지가 도착했어요
          </div>
          <div style={{
            fontSize: 16,
            fontWeight: 400,
            color: "rgba(255,255,255,0.85)",
            lineHeight: 1.8,
            textShadow: "0 1px 2px rgba(60,30,10,0.3)",
            position: "relative",
          }}>
            동물 친구가 하고 싶은<br />
            말이 있대요
          </div>
        </div>

        {/* 기둥 하단 */}
        <div style={{
          width: 16,
          height: 36,
          background: "linear-gradient(180deg, #a07848, #886838)",
          borderRadius: "0 0 4px 4px",
          margin: "0 auto",
          position: "relative",
          top: -4,
          boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
        }} />

        {/* 풀밭 */}
        <div style={{
          fontSize: 20,
          letterSpacing: 4,
          marginTop: -16,
          position: "relative",
          zIndex: 2,
        }}>
          🌿🌱🌿🌱🌿
        </div>
      </div>

      {/* CTA 버튼 */}
      <div style={{ marginTop: 36 }}>
        <button
          onClick={onStart}
          style={{
            width: "100%",
            maxWidth: 320,
            padding: "18px 28px",
            borderRadius: 16,
            background: "linear-gradient(135deg, #c4845a, #d4a070)",
            color: "white",
            fontSize: 19,
            fontWeight: 700,
            fontFamily: "'Jua', sans-serif",
            border: "none",
            cursor: "pointer",
            boxShadow: "0 4px 18px rgba(196,132,90,.35)",
            transition: "all .2s",
          }}
        >
          편지 받으러 가기
        </button>
      </div>

      <div style={{ marginTop: 24, fontSize: 16, fontWeight: 400, color: "#8a7060", lineHeight: 1.8 }}>
        반려동물과의 추억을 알려주시면<br />
        그 친구의 마음을 편지로 전해드려요.
      </div>

      <div style={{ marginTop: 36, fontSize: 14, display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        <a href="/about" onClick={handleNavClick} style={{ color: "#c8b0a0", textDecoration: "underline" }}>
          서비스 소개
        </a>
        <a href="/privacy" onClick={handleNavClick} style={{ color: "#c8b0a0", textDecoration: "underline" }}>
          개인정보처리방침
        </a>
        <a href="/terms" onClick={handleNavClick} style={{ color: "#c8b0a0", textDecoration: "underline" }}>
          이용약관
        </a>
      </div>

      {/* 운영 주체 표기 (사업자 정보) */}
      <div style={{
        marginTop: 28, paddingTop: 18, borderTop: "1px solid #f0e4d8",
        fontSize: 12, fontWeight: 400, color: "#c8b0a0", lineHeight: 1.9,
      }}>
        엔돌핀스튜디오 | 대표: 이유석
        <br />
        사업자등록번호: 775-14-02749
        <br />
        문의: rnak24@gmail.com
      </div>
    </div>
  );
}
